import React, { useState } from 'react';
import {
  Modal, Form, Button, Row, Col, Alert, Spinner, Badge, Card
} from 'react-bootstrap';
import {
  FileSpreadsheet, Calendar, Download, X, Info
} from 'lucide-react';
import exportService from '../../services/exportService';

/**
 * ✅ EXPORT: Modal de sélection de période + déclenchement de l'export Excel.
 * 
 * 3 modes rapides : aujourd'hui / cette semaine / ce mois
 * + Sélection manuelle avec 2 dates.
 */
const ProductsSalesExportModal = ({ show, onHide }) => {

  const todayStr = new Date().toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [selectedPreset, setSelectedPreset] = useState('today');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ===== PRESETS DE PÉRIODE =====

  const applyPreset = (preset) => {
    setSelectedPreset(preset);
    const today = new Date();

    switch (preset) {
      case 'today': {
        const str = today.toISOString().split('T')[0];
        setStartDate(str);
        setEndDate(str);
        break;
      }

      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const str = yesterday.toISOString().split('T')[0];
        setStartDate(str);
        setEndDate(str);
        break;
      }

      case 'week': {
        // Lundi de cette semaine
        const day = today.getDay(); // 0 = dimanche
        const diff = day === 0 ? -6 : 1 - day;
        const monday = new Date(today);
        monday.setDate(today.getDate() + diff);
        setStartDate(monday.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      }

      case 'month': {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        setStartDate(firstDay.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      }

      case 'last_month': {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const endLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        setStartDate(lastMonth.toISOString().split('T')[0]);
        setEndDate(endLastMonth.toISOString().split('T')[0]);
        break;
      }

      default:
        break;
    }
  };

  // ===== VALIDATION =====

  const validate = () => {
    if (!startDate || !endDate) {
      return 'Veuillez saisir les deux dates';
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) {
      return 'La date de fin doit être après la date de début';
    }

    // Limite 1 an
    const oneYearAgo = new Date(start);
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() + 1);
    if (end > oneYearAgo) {
      return 'La période ne peut pas dépasser 1 an';
    }

    return null;
  };

  // ===== EXPORT =====

  const handleExport = async () => {
    setError(null);
    setSuccess(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const result = await exportService.exportProductSalesToExcel(startDate, endDate);

      if (result.success) {
        setSuccess(
          `✅ Export réussi ! ${result.rowsExported} produit(s) exporté(s) dans le fichier ${result.fileName}`
        );
        setTimeout(() => {
          setSuccess(null);
          onHide();
        }, 2500);
      } else {
        setError(result.message || 'Erreur lors de l\'export');
      }
    } catch (err) {
      console.error('❌ Erreur :', err);
      setError(err.message || 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  // ===== MANUEL (change presets → 'custom') =====

  const handleDateChange = (field, value) => {
    setSelectedPreset('custom');
    if (field === 'start') setStartDate(value);
    else setEndDate(value);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton className="bg-success bg-opacity-10">
        <Modal.Title className="d-flex align-items-center gap-2">
          <FileSpreadsheet size={22} className="text-success" />
          Export Excel : CA par produit
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success">
            {success}
          </Alert>
        )}

        <Alert variant="info" className="small d-flex align-items-start gap-2">
          <Info size={16} className="flex-shrink-0 mt-1" />
          <div>
            Génère un fichier Excel avec une ligne par produit : 
            quantité vendue, CA total, prix moyen, nombre de ventes sur la période sélectionnée.
            <br />
            <small className="text-muted">
              Les ventes annulées sont exclues automatiquement.
            </small>
          </div>
        </Alert>

        {/* ===== Raccourcis période ===== */}
        <div className="mb-3">
          <Form.Label className="fw-semibold small mb-2">
            <Calendar size={14} className="me-1" />
            Période rapide
          </Form.Label>
          <div className="d-flex flex-wrap gap-2">
            <PresetButton
              active={selectedPreset === 'today'}
              onClick={() => applyPreset('today')}
              label="Aujourd'hui"
            />
            <PresetButton
              active={selectedPreset === 'yesterday'}
              onClick={() => applyPreset('yesterday')}
              label="Hier"
            />
            <PresetButton
              active={selectedPreset === 'week'}
              onClick={() => applyPreset('week')}
              label="Cette semaine"
            />
            <PresetButton
              active={selectedPreset === 'month'}
              onClick={() => applyPreset('month')}
              label="Ce mois"
            />
            <PresetButton
              active={selectedPreset === 'last_month'}
              onClick={() => applyPreset('last_month')}
              label="Mois dernier"
            />
          </div>
        </div>

        {/* ===== Sélection manuelle ===== */}
        <Card className="border-secondary border-opacity-25">
          <Card.Body>
            <div className="small text-muted mb-3 d-flex align-items-center gap-2">
              <Calendar size={14} />
              Ou choisis manuellement
              {selectedPreset === 'custom' && (
                <Badge bg="primary" pill>Personnalisé</Badge>
              )}
            </div>

            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">Du</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    max={todayStr}
                    onChange={(e) => handleDateChange('start', e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small">Au</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    max={todayStr}
                    min={startDate}
                    onChange={(e) => handleDateChange('end', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={onHide}
          disabled={loading}
        >
          <X size={16} className="me-1" />
          Fermer
        </Button>

        <Button
          variant="success"
          onClick={handleExport}
          disabled={loading || !startDate || !endDate}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Génération...
            </>
          ) : (
            <>
              <Download size={16} className="me-1" />
              Télécharger Excel
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ===== Composant bouton preset =====

const PresetButton = ({ active, onClick, label }) => (
  <Button
    variant={active ? 'primary' : 'outline-primary'}
    size="sm"
    onClick={onClick}
  >
    {label}
  </Button>
);

export default ProductsSalesExportModal;